/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { colors } from '@farmlands/mui-theme';
import { Gradients } from '../gradients';

const GAP_SIZE = 8;
interface BackgroundProps {
  background?: string;
  height?: number;
}

interface Props extends BackgroundProps {
  foreground?: string;
  percentage: number;
  total?: number;
  gradient?: keyof typeof Gradients;
}

const Wrapper = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Label = styled(Typography)``;

const Background = styled('div')<BackgroundProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${GAP_SIZE}px;
  width: 100%;
  border-radius: 16px;
  height: ${({ height }) => height}px;
  background: ${({ background }) => background};
`;

const Foreground = styled('div')<Omit<Props, 'percentage'> & { percentage: string | number }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: ${({ percentage }) => percentage};
  border-radius: 16px;
  height: ${({ height }) => height}px;
  background: ${({ foreground, gradient }) => gradient ?? foreground};
`;

const PercentageLabel = styled('label')<{ color: string; paddingLeft: number }>`
  ${({ theme }) => theme.typography.caption}
  padding-left: ${({ paddingLeft }) => `${paddingLeft}px`};
  color: ${({ color }) => color};
`;

const PercentageMarkers = styled('div')`
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
`;

export const PercentageBar = (_props: Props) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const prop: Props = {
    ..._props,
    height: _props.height ?? 30,
    gradient: Gradients[_props.gradient as string],
    background: _props.background ?? colors.primary100,
    foreground: _props.foreground ?? colors.primary500,
    percentage: _props.percentage,
  };
  const labelSwitchPoint = isSmall ? 35 : 25;

  return (
    <Background {...prop}>
      {prop.percentage > 0 && (
        <Foreground {...prop} percentage={`${prop.percentage}%`}>
          {prop.percentage > labelSwitchPoint && (
            <PercentageLabel color={theme.colors.shade0} paddingLeft={16}>
              {prop.percentage}% Fulfilled
            </PercentageLabel>
          )}
        </Foreground>
      )}
      {prop.percentage <= labelSwitchPoint && (
        <PercentageLabel
          color={theme.colors.primary500}
          paddingLeft={prop.percentage === 0 ? 16 : 0}
        >
          {prop.percentage}% Fulfilled
        </PercentageLabel>
      )}
    </Background>
  );
};

interface MultiProps extends BackgroundProps {
  values: Props[];
  totalFulfilled?: number;
}

export const MultiPercentageBar = (_props: MultiProps) => {
  const prop = _props.values.map((e) => ({
    ...e,
    height: _props.height ?? 30,
    gradient: Gradients[e.gradient as string],
    background: _props.background ?? colors.primary100,
    foreground: e.foreground ?? colors.primary500,
    percentage: e.percentage,
  }));

  const fulfilledPercentage = _props.totalFulfilled ?? prop.reduce((a, v) => a + v.percentage, 0);

  return (
    <Wrapper>
      <Background {..._props}>
        {prop.map((value, index) => {
          return (
            <Foreground
              className='Foreground'
              key={`${value.percentage}-${value.foreground}`}
              {...value}
              percentage={
                index === prop.length - 1
                  ? `calc(${value.percentage}%)`
                  : `calc(${value.percentage}% - ${GAP_SIZE}px)`
              }
            />
          );
        })}
      </Background>
      <PercentageMarkers>
        <Label variant='body2' color={colors.grey700}>
          {`${fulfilledPercentage.toFixed()}%`}
        </Label>
        <Label variant='body2' color={colors.grey700}>
          {`${Math.max(0, 100 - fulfilledPercentage).toFixed()}%`}
        </Label>
      </PercentageMarkers>
    </Wrapper>
  );
};

export default PercentageBar;
